import { useTranslation } from 'react-i18next';
import { ReferenceDataEntry } from 'entities/document';
import { useMemo, useState } from 'react';
import { Transforms } from 'slate';
import { DefaultTable } from 'app/utils/table';
import { ReactEditor, useSlateStatic, RenderElementProps } from 'slate-react';
import { ReferenceTableElement } from 'slate/types/slate';
import { useNavigate } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import { showOriginalValueReferenceTable } from 'slate/utils';
import { usePermissions } from 'app/api/permission-queries';
import { useEditorScope } from 'app/state/contexts/EditorScopeContext';
import { DeleteBlockModal } from 'slate/components/DeleteBlockModal';
import { useEditorReferences } from 'app/api/documents/useEditorData';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

type ReferenceTableProps = RenderElementProps & {
  diff?: unknown;
  side?: string;
  readOnly?: boolean;
};

export const ReferenceTable = (props: ReferenceTableProps) => {
  const { t } = useTranslation('block');

  const editor = useSlateStatic();
  const { attributes, element, children, diff, side, readOnly } = props;
  const refElement = element as ReferenceTableElement;
  const references = useEditorReferences();
  const locale = useEditorLocale();
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { data: pDocument } = useEditorDocument();
  const section = useEditorLiveSection();
  const showOriginalValue = useMemo(() => {
    return showOriginalValueReferenceTable(pDocument, section, element);
  }, [pDocument, section, element]);
  const { documentPermissions, sectionPermissions } = usePermissions(pDocument.superId);
  const hasWritePermission = sectionPermissions[useEditorScope()?.sectionId ?? ''] === 'WRITE';
  const content = useMemo(() => {
    return references.find((r) => r.superId === refElement.referenceDataSuperId);
  }, [references, refElement.referenceDataSuperId]);

  const elementId = element.uuid ? `xrp-tableid-${element.uuid}` : undefined;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const tableCssClasses = useMemo(() => {
    const refEntry = pDocument?.content?.referenceData?.find((r: ReferenceDataEntry) => r.superId === refElement.referenceDataSuperId);
    const cssClass = refEntry?.cssClass;
    if (!cssClass) return '';
    return cssClass
      .split(',')
      .map((c: string) => c.trim())
      .filter(Boolean)
      .join(' ');
  }, [pDocument, refElement.referenceDataSuperId]);
  if (!content) {
    return (
      <div {...attributes}>
        <div style={{ userSelect: 'none' }} contentEditable={false}>
          No data!
        </div>
      </div>
    );
  }

  const performDelete = () => {
    const path = ReactEditor.findPath(editor, props.element);
    Transforms.select(editor, path);
    Transforms.removeNodes(editor, { at: path });
  };
  const confirmDelete = () => {
    setShowDeleteModal(false);
    performDelete();
  };
  const navigateToReferenceData = () => {
    navigate({
      to: '/documents/$documentId/reference-data/$refId',
      params: { documentId, refId: String(refElement.referenceDataSuperId) },
      replace: true,
    });
  };
  const metadata = {
    'data-element-super-id': refElement.referenceDataSuperId,
    'data-document-revision-id': content.revisionId,
    'data-element-revision-id': `${refElement.referenceDataSuperId}_${content.revisionId}`,
    'data-element-uuid': refElement.uuid,
  };

  return (
    <div className="editor-table--container" {...attributes}>
      {!readOnly && (
        <div className="controls--container">
          {hasWritePermission && (
            <button className="controls--btn--danger" onClick={() => setShowDeleteModal(true)}>
              <i className="fa-regular fa-trash-can" />
            </button>
          )}
          {documentPermissions.includes('DB') && (
            <button onClick={navigateToReferenceData}>
              <i className="fa-regular fa-table" />
            </button>
          )}
        </div>
      )}

      <DeleteBlockModal show={showDeleteModal} message={t('delete.modal.table')} onConfirm={confirmDelete} onCancel={() => setShowDeleteModal(false)} />

      <div {...metadata}>
        <DefaultTable id={elementId} data={content} locale={locale} showOriginalValue={showOriginalValue} diff={diff} side={side} cssClass={tableCssClasses} />
        {children}
      </div>
    </div>
  );
};
